import type { ReactElement } from 'react';
import { Chip, Icon } from '@betrybe/ada-react';
import { Link } from 'react-router-dom';
import { useClient } from '@/hooks';
import { canEditGuarantorData } from '@/utils';
import StatesEnum from '@/utils/enums/StatesEnum';

type PersonCardProps = {
  name: string;
  person: string;
  maritalStatus: string;
  kinship?: string;
  phone?: string;
  email?: string;
  id: string;
  isReproved?: boolean;
  cpf: string;
  rg: string;
  address: string;
  residenceNumber: string;
  neighborhood: string;
  guarantorId?: string;
};

function PersonCard({
  name,
  person,
  maritalStatus,
  kinship,
  phone,
  email,
  id,
  isReproved,
  cpf,
  rg,
  address,
  residenceNumber,
  neighborhood,
  guarantorId,
}: PersonCardProps): ReactElement {
  const { currentState, scoreDisapprovedGuarantorId } = useClient();

  const getEnumText = (kinshipEnum: string): string => {
    switch (kinshipEnum) {
      case 'not_relative':
        return 'Não é parente';

      case 'father_or_mother':
        return 'Pai ou mãe';

      case 'uncle_or_aunt':
        return 'Tio ou tia';

      case 'grand_father_or_mother':
        return 'Avô ou avó';

      case 'other':
        return 'Outro';

      default:
        return kinshipEnum;
    }
  };

  const getMaritalStatusText = (maritalStatusEnum: string): string => {
    switch (maritalStatusEnum) {
      case 'single':
        return 'Pessoa solteira';

      case 'married':
        return 'Pessoa casada';

      case 'divorced':
        return 'Pessoa divorciada';

      case 'widower':
        return 'Pessoa viúva';

      default:
        return maritalStatusEnum;
    }
  };

  const redirectToForm = (idKey: string, personType: string) => {
    if (personType === 'guarantor') {
      if (idKey.split('-')[2] === '0') {
        return '/register/first-person-guarantor';
      }
      if (idKey.split('-')[2] === '1') {
        return '/register/second-person-guarantor';
      }
    }
    if (personType === 'candidate') {
      return '/register';
    }
    return '';
  };

  const canEdit = () => {
    if (
      person === 'guarantor' &&
      canEditGuarantorData.indexOf(currentState as StatesEnum) >= 0
    ) {
      if (
        currentState === StatesEnum.CREDIT_REGISTRATION_COMPLETED &&
        guarantorId === scoreDisapprovedGuarantorId
      ) {
        return false;
      }
      return true;
    }
    if (
      person === 'candidate' &&
      currentState === StatesEnum.SCORE_REGISTRATION_COMPLETED
    ) {
      return true;
    }
    return false;
  };

  return (
    <div className="guarantors-card-container" key={id}>
      {isReproved && (
        <div className="guarantors-card-container__chip-div">
          <Chip
            color="#B01726"
            backgroundColor="#FBE5DF"
            className="guarantors-card-container__chip-div--chip"
          >
            <Icon height={15} color="#B01726" name="clear" />
            <p>Pessoa reprovada</p>
          </Chip>
        </div>
      )}
      <div className="guarantors-card-container__guarantors-name">
        <p>{name}</p>
        {canEdit() && (
          <Link to={redirectToForm(id, person)}>
            <Icon color="#003BE5" name="edit" />
          </Link>
        )}
      </div>
      <div className="guarantors-card-container__guarantors-status">
        {person === 'candidate' ? 'Pessoa candidata' : 'Pessoa avalista'}
      </div>
      <div className="guarantors-card-container__divider" />
      <div className="guarantors-card-container__guarantors-info">
        <div className="guarantors-card-container__guarantors-info--info-title">
          Estado civil:
        </div>
        <div>{getMaritalStatusText(maritalStatus)}</div>
      </div>
      {kinship && (
        <div className="guarantors-card-container__guarantors-info">
          <div className="guarantors-card-container__guarantors-info--info-title">
            Parentesco:
          </div>
          <div>{getEnumText(kinship)}</div>
        </div>
      )}
      {phone && (
        <div className="guarantors-card-container__guarantors-info">
          <div className="guarantors-card-container__guarantors-info--info-title">
            Telefone:
          </div>
          <div>{phone}</div>
        </div>
      )}
      {email && (
        <div className="guarantors-card-container__personal-info">
          <div className="guarantors-card-container__guarantors-info--info-title">
            E-mail:
          </div>
          <div>{email}</div>
        </div>
      )}
      <div className="guarantors-card-container__personal-info">
        <div className="guarantors-card-container__guarantors-info--info-title">
          Endereço:
        </div>
        <div>{[address, ', ', residenceNumber, ', ', neighborhood]}</div>
      </div>
      <div className="guarantors-card-container__personal-info">
        <div className="guarantors-card-container__guarantors-info--info-title">
          CPF:
        </div>
        <div>{cpf}</div>
      </div>
      <div className="guarantors-card-container__personal-info">
        <div className="guarantors-card-container__guarantors-info--info-title">
          RG:
        </div>
        <div>{rg}</div>
      </div>
    </div>
  );
}

PersonCard.defaultProps = {
  kinship: '',
  phone: '',
  email: '',
  isReproved: false,
  guarantorId: '',
};

export default PersonCard;
